<template>
  <form class="accreditation" @submit.prevent >
    <div class="a-container-form ">
      <div class="a-label ">{{$t('form.accreditation.status')}}:</div>
      <p class="a-input a-input--disabled " v-t="acc.state.label"></p>
    </div>
    <div class="a-container-form a-container-form--align-start">
      <div class="a-label ">{{$t('form.accreditation.filled-request')}}:</div>
      <p class="a-input a-input--disabled ">
        № {{acc.id}}
        <span type="button"
              class="gui-btn-none-style"
              @click="onAccreditationSignCheckClick"
              v-if="acc.hasSign">
            <img class="accreditation__reg-img" src="@/assets/img/static/b-icon_edit.svg" alt="">
        </span>
        <br><br>
        <span class="accreditation__error-doc" v-if="acc.reason && acc.state.DECLINED">{{$t('labels.reason')}}: {{acc.reason}}</span>
      </p>
    </div>
    <div class="a-container-form a-container-form--align-start" v-if="acc.expired">
      <div class="a-label ">{{$t('form.accreditation.due-date')}}:</div>
      <date-time class="a-input a-input--disabled" :value="acc.expired" :time="false"></date-time>
    </div>
    <div class="a-container-form a-container-form--align-start u-padding-bottom-0" v-if="acc.documents">
      <div class="a-label ">{{$t('form.accreditation.documents')}}:</div>
      <div class="accreditation__file-group">
        <div class="a-input-file-block accreditation__block" v-for="(doc, idx) in acc.documents" :key="doc.id">
          <p class="a-input a-input--disabled ">{{idx + 1}}. {{doc.name}}
            <span class="accreditation__span-sms" v-if="doc.state === 'Declined'">
              <img src="@/assets/img/static/b-icon_disable.svg" alt="">
              {{$t('accreditation-document-state.declined')}}
            </span>
          </p>

          <div class="a-container-form a-mandatory" v-if="doc.passport">
            <div class="a-label">{{$t('form.accreditation.document-and-signature-expiration-date')}}:</div>

            <div class="a-form-field"
                 :class="{ 'a-input-group--error': $v.acc.documents.$each[idx].expirationDate.$error }"
                 v-if="permissions.CAN_EDIT_ACCREDITATION">

              <div class="a-input-group">
                <div class="a-input-group__prepend ">
                  <img src="@/assets/img/static/vector.svg" alt="">
                </div>
                <date-picker :value="doc.expirationDate"
                             @input="onDocDateChanged(doc.id, $event)">
                </date-picker>
              </div>
              <span class="a-helper-text a-validation-error" v-if="!$v.acc.documents.$each[idx].expirationDate.filled">{{$t('labels.mandatory-field')}}</span>
            </div>

            <date-time class="a-input a-input--disabled"
                       :value="doc.expirationDate"
                       v-else>
            </date-time>
          </div>

            <file-picker :mode="doc.files.length === 0 ? 'link' : 'button'"
                         @prepared="onFileSignClick($event, doc.id)"
                         v-if="permissions.CAN_EDIT_ACCREDITATION">
            </file-picker>

            <file-link :name="file.name"
                       :size="file.size"
                       :link="externalLink(file.uid)"
                       :key="file.uid"
                       :removable="permissions.CAN_EDIT_ACCREDITATION"
                       :signed="!!file.hasSign"
                       @remove="onFileRemoveClick(doc.id, file.id)"
                       @onsigncheck="onFileSignCheckClick(doc.id, file.id)"
                       v-for="file in doc.files">
            </file-link>

          <div class="accreditation__error-doc" v-if="doc.reason">{{$t('labels.reason')}}: {{doc.reason}}</div>
        </div>
      </div>
    </div>
    <div class="a-container-form" v-if="permissions.CAN_SIGN_ACCREDITATION">
      <div class="a-label ">{{$t('labels.sign-accreditation')}}:</div>
      <button type="button" class="gui-btn gui-btn--white" @click="onSignAccreditationClick">{{$t('buttons.digitally-sign')}}</button>
    </div>
    <div class="a-container-form" v-if="permissions.CAN_REJECT_ACCREDITATION">
      <button type="button" class="gui-btn gui-btn--red gui-btn--w-184" @click="onRejectClick">{{$t('buttons.reject-accreditation')}}</button>
    </div>

    <signature-details-dialog ref="signCheck"></signature-details-dialog>

    <signing-dialog ref="signAccreditation"
                    @signed="signAccreditation">
    </signing-dialog>

    <loading v-if="loading"></loading>
  </form>
</template>

<script>
  import Errors from '@/api/types/errors'
  import SigningDialog from '@/components/common/dialogs/SigningDialog';
  import SignatureDetailsDialog from '@/components/common/dialogs/SignatureDetailsDialog'
  import { requiredIf } from '@/validators'

  export default {
    components: {
      SigningDialog,
      SignatureDetailsDialog
    },

    props: {
      acc: {
        type: Object,
        mandatory: true
      }
    },

    data: () => ({
      signingHtml: undefined
    }),

    computed: {
      permissions() { return this.acc.permissions },
    },

    mounted() {
      this.$v.$touch();
    },

    validations: {
      acc: {
        documents: {
          $each: {
            expirationDate: {
              filled: requiredIf(item => {
                return !!item.passport;
              })
            }
          }
        }
      }
    },

    methods: {
      async onSignAccreditationClick() {
        this.runAction(async () => {
          try {
            this.signingHtml = await this.$store.dispatch('getSigningText')
            this.$refs.signAccreditation.open({ text: this.signingHtml });
          }
          catch(e) {
            console.error('unable to get signing data for accreditation', e);
            this.globalErrorHandler(e);
          }
        });
      },

      async signAccreditation(result) {
        this.runAction(async () => {
          try {
            this.$refs.signAccreditation.close();

            await this.$store.dispatch('signAccreditation', {
              text: result.data,
              signature: result.signature
            });
          }
          catch(e) {
            console.error('unable to sign accreditation', e);
            this.globalErrorHandler(e);
          }
        });
      },

      async onRejectClick() {
        this.runAction(async() => {
          try {
            await this.$store.dispatch('rejectAccreditation')
          }
          catch(e) {
            console.error('unable to reject accreditation', e);
            this.globalErrorHandler(e);
          }
        });
      },

      async onFileSignClick(file, documentId) {
        this.runAction(async () => {
          try {
            await this.$store.dispatch('attachFile', {
              documentId,
              sign: file.sign,
              file: file.file
            });
          }
          catch(e) {
            if (e == Errors.INVALID_FILE) {
              if (e.details.maxSize === 0) {
                this.showError({
                  error: 'errors.unsupported-file-extension'
                });
              }
              else if (e.details.maxSize < e.details.size) {
                this.showError({
                  error: 'errors.too-big-file'
                });
              }
              else {
                this.globalErrorHandler(e);
              }
            }
            else {
              this.globalErrorHandler(e);
            }
          }
        });
      },

      async onFileRemoveClick(documentId, fileId) {
        this.runAction(async() => {
          try {
            await this.$store.dispatch('removeFile', { documentId, fileId});
          }
          catch(e) {
            console.error('unable to remove file attached to accreditation', e);
            this.globalErrorHandler(e);
          }
        });
      },

      async onDocDateChanged(documentId, date) {
        this.runAction(async () => {
          try {
            await this.$store.dispatch('setAccreditationDocumentExpirationDate', {
              documentId,
              date
            });
          }
          catch(e) {
            console.error('unable to change expiration date of attached document', e);
            this.globalErrorHandler(e);
          }
        });
      },

      async onFileSignCheckClick(documentId, fileId) {
        this.runAction(async () => {
          try {
            const signature = await this.$store.dispatch('checkAccountFileSignature', {
              userId: this.acc.owner,
              accreditationId: this.acc.id,
              docId: documentId,
              fileId: fileId
            });

            this.$refs.signCheck.open(signature);
          }
          catch(e) {
            console.error('unable to check signature of attached file', e);
            this.globalErrorHandler(e);
          }
        })
      },

      async onAccreditationSignCheckClick() {
        this.runAction(async () => {
          try {
            const signature = await this.$store.dispatch('checkAccountAccreditationSignature', {
              userId: this.acc.owner,
              accreditationId: this.acc.id
            });

            this.$refs.signCheck.open(signature);
          }
          catch(e) {
            console.error('unable to check signature of accreditation', e);
            this.globalErrorHandler(e);
          }
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .a-mandatory {
    .a-label::before {
      content: '*';
      color: #cc061a;
    }
  }
</style>
